<template>
  <div class="dashboard">
    <MobileHeader @toggle-sidebar="toggleSidebar" />
    
    <Sidebar 
      :isOpen="sidebarOpen" 
      :isMobile="isMobile" 
      @close="closeSidebar"
    />
    
    <main class="main-content" :class="{ 'main-content-mobile': isMobile }">
      <header class="header">
        <h1>Управление на потребители</h1>
        <div class="header-actions">
          <div class="search-box">
            <i class="fas fa-search"></i>
            <input
              type="text"
              v-model="searchTerm"
              placeholder="Търсене на потребители..."
              class="search-input"
            />
          </div>
          <!-- Mobile Church Filter -->
          <div v-if="isMobile" class="mobile-filter">
            <select v-model="selectedChurch" class="form-control filter-select">
              <option value="">Всички църкви</option>
              <option v-for="church in churches" :key="church.id" :value="church.name.bg">
                {{ church.name.bg }}
              </option>
            </select>
          </div>
        </div>
      </header>
      
      <div class="content">
        <div class="card">
          <div class="card-header">
            <h3>Всички потребители</h3>
            <div v-if="!isMobile" class="filters">
              <select v-model="selectedChurch" class="form-control filter-select">
                <option value="">Всички църкви</option>
                <option v-for="church in churches" :key="church.id" :value="church.name.bg">
                  {{ church.name.bg }}
                </option>
              </select>
            </div>
          </div>
          
          <div v-if="loading" class="loading">
            <i class="fas fa-spinner fa-spin"></i>
            Зареждане на потребители...
          </div>
          
          <div v-else-if="filteredUsers.length === 0" class="no-data">
            <i class="fas fa-users"></i>
            <p>Няма намерени потребители</p>
          </div>
          
          <div v-else>
            <!-- Mobile Users Grid -->
            <div v-if="isMobile" class="users-grid">
              <div
                v-for="user in paginatedUsers"
                :key="user.id"
                class="user-card"
              >
                <div class="user-info">
                  <h4 class="user-name">{{ user.fullName || user.name || 'N/A' }}</h4>
                  <p class="user-email">{{ user.email }}</p>
                  
                  <div class="user-details">
                    <div class="detail-item">
                      <i class="fas fa-church"></i>
                      <span>{{ getChurchName(user.churchId || user.selectedChurch) }}</span>
                    </div>
                    
                    <div class="detail-item">
                      <i class="fas fa-calendar"></i>
                      <span>{{ formatDate(user.createdAt) }}</span>
                    </div>
                    
                    <div class="detail-item">
                      <i class="fas fa-circle" :class="user.active ? 'text-success' : 'text-danger'"></i>
                      <span>{{ user.active ? 'Активен' : 'Неактивен' }}</span>
                    </div>
                  </div>
                </div>
                
                <div class="user-actions" v-if="user.email !== currentUserEmail">
                  <button
                    @click="viewUser(user)"
                    class="btn btn-sm btn-primary"
                    title="Преглед на детайли"
                  >
                    <i class="fas fa-eye"></i>
                  </button>
                  <button
                    @click="toggleUserStatus(user)"
                    :class="['btn', 'btn-sm', user.active ? 'btn-danger' : 'btn-success']"
                    :title="user.active ? 'Деактивиране' : 'Активиране'"
                  >
                    <i :class="user.active ? 'fas fa-ban' : 'fas fa-check'"></i>
                  </button>
                  <button
                    @click="showDeleteDialog(user)"
                    class="btn btn-sm btn-danger"
                    title="Изтриване на потребител"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Desktop Table -->
            <div v-else class="table-container">
            <table class="table">
              <thead>
                <tr>
                  <th>Име</th>
                  <th>Имейл</th>
                  <th>Църква</th>
                  <th>Създаден на</th>
                  <th>Статус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in paginatedUsers" :key="user.id">
                  <td>
                    <div class="user-info">
                      <span>{{ user.fullName || user.name || 'N/A' }}</span>
                    </div>
                  </td>
                  <td>{{ user.email }}</td>
                  <td>
                    <span class="church-badge church-badge-large">
                      {{ getChurchName(user.churchId || user.selectedChurch) }}
                    </span>
                  </td>
                  <td>{{ formatDate(user.createdAt) }}</td>
                  <td>
                    <span :class="['status-badge', 'status-badge-large', user.active ? 'status-active' : 'status-inactive']">
                      {{ user.active ? 'Активен' : 'Неактивен' }}
                    </span>
                  </td>
                  <td>
                    <div class="action-buttons" v-if="user.email !== currentUserEmail">
                      <button
                        @click="viewUser(user)"
                        class="btn btn-sm btn-primary"
                        title="Преглед на детайли"
                      >
                        <i class="fas fa-eye"></i>
                      </button>
                      <button
                        @click="toggleUserStatus(user)"
                        :class="['btn', 'btn-sm', user.active ? 'btn-danger' : 'btn-success']"
                        :title="user.active ? 'Деактивиране' : 'Активиране'"
                      >
                        <i :class="user.active ? 'fas fa-ban' : 'fas fa-check'"></i>
                      </button>
                      <button
                        @click="showDeleteDialog(user)"
                        class="btn btn-sm btn-danger"
                        title="Изтриване на потребител"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            </div>
          </div>
          
          <!-- Pagination -->
          <div v-if="filteredUsers.length > itemsPerPage" class="pagination">
            <button
              @click="previousPage"
              :disabled="currentPage === 1"
              class="btn btn-secondary"
            >
              <i class="fas fa-chevron-left"></i>
              Предишна
            </button>
            
            <span class="page-info">
              Страница {{ currentPage }} от {{ totalPages }}
            </span>
            
            <button
              @click="nextPage"
              :disabled="currentPage === totalPages"
              class="btn btn-secondary"
            >
              Следваща
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </main>
    
    <!-- User Details Modal -->
    <div v-if="selectedUser" class="modal-overlay" @click="closeModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>Детайли за потребител</h3>
          <button @click="closeModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="user-detail-grid">
            <div class="detail-item">
              <label>Име:</label>
              <span>{{ selectedUser.fullName || selectedUser.name || 'Няма данни' }}</span>
            </div>
            <div class="detail-item">
              <label>Имейл:</label>
              <span>{{ selectedUser.email }}</span>
            </div>
            <div class="detail-item">
              <label>Избрана църква:</label>
              <span>{{ getChurchName(selectedUser.churchId || selectedUser.selectedChurch) }}</span>
            </div>
            <div class="detail-item">
              <label>Телефон:</label>
              <span>{{ selectedUser.phone || 'Няма данни' }}</span>
            </div>
            <div class="detail-item">
              <label>Създаден на:</label>
              <span>{{ formatDate(selectedUser.createdAt) }}</span>
            </div>
            <div class="detail-item">
              <label>Последно влизане:</label>
              <span>{{ formatDate(selectedUser.lastLogin) }}</span>
            </div>
            <div class="detail-item">
              <label>Статус:</label>
              <span :class="['status-badge', selectedUser.active ? 'status-active' : 'status-inactive']">
                {{ selectedUser.active ? 'Активен' : 'Неактивен' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="cancelDelete">
      <div class="modal delete-modal" @click.stop>
        <div class="modal-header">
          <h3>
            <i class="fas fa-exclamation-triangle text-danger"></i>
            Изтриване на потребител
          </h3>
          <button @click="cancelDelete" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="delete-warning">
            <p class="warning-text">
              <strong>Внимание!</strong> Сигурни ли сте, че искате да изтриете този потребител?
            </p>
            
            <div v-if="userToDelete" class="user-preview">
              <div class="preview-item">
                <i class="fas fa-user"></i>
                <span><strong>Име:</strong> {{ userToDelete.fullName || userToDelete.name || 'Няма данни' }}</span>
              </div>
              <div class="preview-item">
                <i class="fas fa-envelope"></i>
                <span><strong>Имейл:</strong> {{ userToDelete.email }}</span>
              </div>
              <div v-if="userToDelete.churchId || userToDelete.selectedChurch" class="preview-item">
                <i class="fas fa-church"></i>
                <span><strong>Църква:</strong> {{ getChurchName(userToDelete.churchId || userToDelete.selectedChurch) }}</span>
              </div>
            </div>
            
            <div class="danger-notice">
              <i class="fas fa-exclamation-circle"></i>
              <span>Това действие е необратимо и не може да бъде отменено!</span>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="cancelDelete" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Отказ
          </button>
          <button @click="confirmDelete" class="btn btn-danger">
            <i class="fas fa-trash"></i>
            Изтрий потребителя
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { database, auth } from '../firebase'
import { ref as dbRef, onValue, update } from 'firebase/database'
import moment from 'moment'
import Sidebar from './Sidebar.vue'
import MobileHeader from './MobileHeader.vue'

export default {
  name: 'UsersPage',
  components: {
    Sidebar,
    MobileHeader
  },
  data() {
    return {
      loading: true,
      users: {},
      churchesData: {},
      searchTerm: '',
      selectedChurch: '',
      selectedUser: null,
      currentPage: 1,
      itemsPerPage: 10,
      sidebarOpen: false,
      isMobile: false,
      unsubscribers: [],
      // Delete confirmation modal
      showDeleteModal: false,
      userToDelete: null
    }
  },
  computed: {
    currentUserEmail() {
      return auth.currentUser?.email || ''
    },
    usersList() {
      return Object.entries(this.users).map(([id, user]) => ({
        id,
        ...user,
        active: user.active !== false // Default to true if not specified
      }))
    },
    churches() {
      return Object.entries(this.churchesData).map(([id, church]) => ({
        id,
        ...church
      })).sort((a, b) => a.name.bg.localeCompare(b.name.bg))
    },
    filteredUsers() {
      let filtered = this.usersList

      // Filter by search term
      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase()
        filtered = filtered.filter(user => {
          const fullName = (user.fullName || user.name || '').toLowerCase()
          const email = (user.email || '').toLowerCase()
          const churchName = this.getChurchName(user.churchId || user.selectedChurch).toLowerCase()
          
          return fullName.includes(term) || 
                 email.includes(term) || 
                 churchName.includes(term)
        })
      }

      // Filter by church
      if (this.selectedChurch) {
        filtered = filtered.filter(user => {
          const userChurchName = this.getChurchName(user.churchId || user.selectedChurch)
          return userChurchName === this.selectedChurch
        })
      }

      return filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    },
    totalPages() {
      return Math.ceil(this.filteredUsers.length / this.itemsPerPage)
    },
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredUsers.slice(start, end)
    }
  },
  methods: {
    getChurchName(churchId) {
      if (!churchId) return 'Не е избрана'
      const church = this.churchesData[churchId]
      return church ? church.name.bg : 'Неизвестна църква'
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen
    },
    closeSidebar() {
      this.sidebarOpen = false
    },
    formatDate(timestamp) {
      if (!timestamp) return 'Няма данни'
      return moment(timestamp).format('MMM DD, YYYY HH:mm')
    },
    viewUser(user) {
      this.selectedUser = user
    },
    closeModal() {
      this.selectedUser = null
    },
    async toggleUserStatus(user) {
      try {
        const newStatus = !user.active
        await update(dbRef(database, `users/${user.id}`), {
          active: newStatus
        })
        
        // Update local state
        this.users[user.id].active = newStatus
      } catch (error) {
        console.error('Error updating user status:', error)
      }
    },
    showDeleteDialog(user) {
      this.userToDelete = user
      this.showDeleteModal = true
    },
    cancelDelete() {
      this.showDeleteModal = false
      this.userToDelete = null
    },
    async confirmDelete() {
      if (!this.userToDelete) return
      
      try {
        // Call Firebase Function to delete from both Auth and Database
        const response = await fetch('https://europe-west1-novjivot-church.cloudfunctions.net/deleteUser', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: this.userToDelete.id,
            userEmail: this.userToDelete.email
          })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Failed to delete user');
        }

        console.log('User deletion result:', result);
        
        // Remove from local state
        delete this.users[this.userToDelete.id]
        
        // Close modal if the deleted user was selected
        if (this.selectedUser && this.selectedUser.id === this.userToDelete.id) {
          this.selectedUser = null
        }
        
        // Close delete modal
        this.showDeleteModal = false
        this.userToDelete = null
        
        // Show success message
      } catch (error) {
        console.error('Error deleting user:', error)
        alert(`Възникна грешка при изтриването на потребителя: ${error.message}`)
      }
    },
    async deleteUser(user) {
      // This method is now replaced by the modal system
      // Keeping for backwards compatibility, but redirecting to modal
      this.showDeleteDialog(user)
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },
    loadUsers() {
      const usersRef = dbRef(database, 'users')
      this.unsubscribers.push(onValue(usersRef, (snapshot) => {
        this.users = snapshot.val() || {}
        this.loading = false
      }))
    },
    loadChurches() {
      const churchesRef = dbRef(database, 'churches')
      this.unsubscribers.push(onValue(churchesRef, (snapshot) => {
        this.churchesData = snapshot.val() || {}
      }))
    }
  },
  mounted() {
    this.loadUsers()
    this.loadChurches()
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
    this.unsubscribers.forEach((unsubscribe) => unsubscribe())
  }
}
</script>

<style scoped>
.dashboard {
  display: flex;
  min-height: 100vh;
  flex-direction: column;
}

.main-content {
  flex: 1;
  margin-left: 260px;
  background: #F8F8F8;
}

.main-content-mobile {
  margin-left: 0;
}

.header {
  background: white;
  padding: 24px 32px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header h1 {
  color: #333333;
  font-size: 28px;
  font-size: min(max(20px, 4vw), 28px);
  font-weight: 700;
}

.header-actions {
  display: flex;
  gap: 16px;
  align-items: center;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-box i {
  position: absolute;
  left: 12px;
  color: #6b7280;
}

.search-input {
  padding: 8px 12px 8px 36px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  width: 300px;
  font-size: 14px;
}

.search-input:focus {
  outline: none;
  border-color: #1D687B;
  box-shadow: 0 0 0 3px rgba(29, 104, 123, 0.1);
}

.content {
  padding: 32px 16px;
}

.card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 24px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.card-header h3 {
  color: #333333;
  font-size: 20px;
  font-weight: 600;
}

.filters {
  display: flex;
  gap: 12px;
}

.filter-select {
  width: 200px;
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
}

.filter-select:focus {
  outline: none;
  border-color: #1D687B;
}

.form-control {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 12px;
}

.form-control:focus {
  outline: none;
  border-color: #1D687B;
  box-shadow: 0 0 0 3px rgba(29, 104, 123, 0.1);
}

.loading {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
}

.loading i {
  font-size: 24px;
  margin-bottom: 12px;
  color: #1D687B;
}

.no-data {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
}

.no-data i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.3;
}

.table-container {
  overflow-x: auto;
}

.table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
}

.table th,
.table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.table th {
  background: #F8F8F8;
  color: #333333;
  font-weight: 600;
  font-size: 14px;
}

.table td {
  color: #333333;
}

.table tbody tr:hover {
  background: #f9fafb;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.church-badge {
  background: rgba(121, 218, 242, 0.2);
  color: #1D687B;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.church-badge-large {
  font-size: 14px;
  padding: 8px 12px;
  font-weight: 600;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge-large {
  font-size: 14px;
  padding: 8px 12px;
  font-weight: 600;
}

.status-active {
  background: #d1fae5;
  color: #065f46;
}

.status-inactive {
  background: #fee2e2;
  color: #991b1b;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #1D687B;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #155660;
}

.btn-secondary {
  background: #79daf2;
  color: #1D687B;
}

.btn-secondary:hover:not(:disabled) {
  background: #66c7e0;
}

.btn-success {
  background: #10b981;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #059669;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
}

.btn-sm {
  padding: 10px 14px;
  font-size: 14px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.page-info {
  color: #6b7280;
  font-size: 14px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  margin: 20px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #F8F8F8;
}

.modal-header h3 {
  color: #333333;
  font-size: 20px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 10px;
  border-radius: 8px;
  transition: color 0.3s ease;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.close-btn:hover {
  color: #374151;
  background-color: #f3f4f6;
}

.modal-body {
  padding: 24px;
}

.user-detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-item label {
  font-weight: 600;
  color: #333333;
  font-size: 14px;
}

.detail-item span {
  color: #6b7280;
}

.current-user-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
}

.badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-info {
  background: #e0f2fe;
  color: #0277bd;
  border: 1px solid #b3e5fc;
}

@media (max-width: 768px) {
  .card {
    padding: 0;
    box-shadow: none;
    border-radius: 0;
    background-color: transparent;
  }

  .card-header {
    display: none;
  }
  
  .main-content {
    margin-left: 0;
  }
  
  .header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .search-input {
    width: 100%;
  }
  
  .header-actions {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions > div {
    width: 100%;
  }
  
  .mobile-filter .filter-select {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: white;
    font-size: 16px;
    color: #374151;
  }
  
  .mobile-filter .filter-select:focus {
    outline: none;
    border-color: #1D687B;
    box-shadow: 0 0 0 3px rgba(29, 104, 123, 0.1);
  }
  
  .card-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .user-detail-grid {
    grid-template-columns: 1fr;
  }
  
  .table-container {
    overflow-x: scroll;
  }
  
  .users-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .user-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
    background: white;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .user-card .user-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  
  .user-name {
    color: #333333;
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 4px 0;
  }
  
  .user-email {
    color: #6b7280;
    font-size: 14px;
    margin: 0 0 12px 0;
  }
  
  .user-details {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
  }
  
  .detail-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 14px;
    color: #6b7280;
  }
  
  .detail-item i {
    width: 16px;
    min-width: 16px;
    color: #1D687B;
    flex-shrink: 0;
    margin-top: 2px;
  }
  
  .detail-item i.text-success {
    color: #10b981;
  }
  
  .detail-item i.text-danger {
    color: #ef4444;
  }
  
  .detail-item span {
    flex: 1;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  
  .user-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }
}

/* Delete Modal Styles */
.delete-modal {
  max-width: 500px;
}

.delete-warning {
  text-align: center;
}

.warning-text {
  font-size: 16px;
  margin-bottom: 20px;
  color: #374151;
}

.user-preview {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin: 20px 0;
  text-align: left;
}

.preview-item {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.preview-item:last-child {
  margin-bottom: 0;
}

.preview-item i {
  color: #6b7280;
  width: 16px;
}

.preview-item span {
  font-size: 14px;
  color: #374151;
}

.danger-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 12px;
  margin-top: 20px;
  font-size: 14px;
  color: #dc2626;
  font-weight: 500;
}

.danger-notice i {
  color: #dc2626;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.modal-header .text-danger {
  color: #dc2626;
  margin-right: 8px;
}

/* Button styles for the delete modal */
.btn.btn-secondary {
  background: #6b7280;
  color: white;
  border: 1px solid #6b7280;
}

.btn.btn-secondary:hover {
  background: #4b5563;
  border-color: #4b5563;
}

.btn.btn-danger {
  background: #dc2626;
  color: white;
  border: 1px solid #dc2626;
}

.btn.btn-danger:hover {
  background: #b91c1c;
  border-color: #b91c1c;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  border: none;
}

.btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Mobile responsiveness for delete modal */
@media (max-width: 768px) {
  .delete-modal {
    max-width: 90vw;
    margin: 20px;
  }
  
  .modal-footer {
    flex-direction: column;
    gap: 8px;
  }
  
  .modal-footer .btn {
    width: 100%;
    justify-content: center;
  }
  
  .warning-text {
    font-size: 14px;
  }
}
</style>
